import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Typography, ExpandCollapse } from '@telus-uds/components-web';
import {
  useFormRunner,
  renderItems,
  normalizeItems,
  collectLeafFields,
  resolveString
} from '@mobilelive-inc/telus-forms-core';

import { standardVulcanFieldRegistry } from './vulcanFormsCoreAdapters/fieldRegistry';
import {
  vulcanStructuralComponents,
  VulcanSectionChromeContext
} from './vulcanFormsCoreAdapters/vulcanStructuralComponents';
import { vulcanContentTypeMap } from './vulcanFormsCoreAdapters/vulcanContentTypeMap';
import { createVulcanFieldRules } from './vulcanFormsCoreAdapters/vulcanFieldRules';
import { createVulcanPricingAdapter } from './vulcanFormsCoreAdapters/pricingAdapter';
import { createVulcanPayloadAdapter } from './vulcanFormsCoreAdapters/payloadAdapter';
import { createVulcanSectionRegistry } from './vulcanFormsCoreAdapters/vulcanSectionRegistry';
import { createVulcanSubmitHandler } from './vulcanFormsCoreAdapters/submitHandler';
import { VulcanPricingFooter } from './vulcanFormsCoreAdapters/VulcanPricingFooter';
import { VulcanUserEditContext } from './vulcanFormsCoreAdapters/userEditContext';
import { makeHiddenAncestorPredicate } from './vulcanFormsCoreAdapters/hiddenAncestorRule';
import isGatedByEmptyRelatedField from './vulcanFormsCoreAdapters/emptyRelatedFieldGate';
import { validateVulcanStepFields } from './vulcanFormsCoreAdapters/validateVulcanStepFields';
import { secureBusinessPlanVulcan } from '../utils/constant';

const formSectionContentType = 'formSection';

/**
 * Compute the initially-open accordion panels — parity of formStepVulcan's `open`
 * (formStepVulcan.js:304): a section opens if `isPanelInitiallyOpen`, or if any of
 * its fields currently has an error. Derivable statically from the raw step fields
 * + initial errors (no live engine state needed), so it seeds `<ExpandCollapse>`.
 */
const computeOpenPanels = (stepFields, formErrors = {}) =>
  (stepFields || [])
    .filter(
      entry =>
        entry &&
        entry.sys &&
        entry.sys.contentType &&
        entry.sys.contentType.sys &&
        entry.sys.contentType.sys.id === formSectionContentType
    )
    .reduce((acc, section) => {
      const f = section.fields || {};
      if (f.isPanelInitiallyOpen) {
        acc.push(f.name);
      } else if (
        (f.formFields || []).some(
          item => item && item.fields && formErrors[item.fields.name]
        )
      ) {
        acc.push(f.name);
      }
      return acc;
    }, []);

/**
 * Stage 3 capstone — the step-orchestration harness. Composes the whole Vulcan
 * form lifecycle for ONE step out of `@mobilelive-inc/telus-forms-core`'s
 * `<FormRunner>` + config, replacing the hand-written `renderFormFields` loop in
 * `formStepVulcan.js`:
 *   - `normalizeItems` builds the structure-preserving tree (fields, groups,
 *     sections, notes, bespoke/content-type sections);
 *   - the field/section registries + structural components render it;
 *   - fieldRules / pricing / payload are wired as the engine's config hooks;
 *   - the Vulcan collapsible accordion (`ExpandCollapse`) wraps FormRunner, and the
 *     section chrome that lives in formStepVulcan's scope (role-label control,
 *     the non-live visibility context, step-level extras) is handed to the engine's
 *     `Section` via `VulcanSectionChromeContext`. Live section visibility is
 *     derived inside `Section` from the engine `form` (forms-core alpha.5).
 *
 * This is the composition seam; the remaining parity depth (§4.1 submit routing in
 * `onSubmit`, the pricing footer, per-section interaction parity) is layered on by
 * the caller via `onSubmit` / `renderFooter` and the section registry.
 *
 * `renderChrome` (forms-core alpha.6) forwards straight to the package's
 * `<FormRunner renderChrome>` — pass it to embed the field/section tree inside a
 * HOST's own existing chrome (its own card wrapper, its own real pricing footer,
 * its own nav) instead of the engine's default `<form>`-based chrome. When given,
 * `renderFooter` is ignored (call your own footer with the engine `renderChrome`
 * hands you instead).
 */
export function VulcanFormStepRunner({
  stepFields,
  stepName,
  step,
  steps,
  orderData,
  profile,
  locale,
  strings,
  dealershipName,
  initialValues,
  formErrors,
  onSubmit,
  renderFooter,
  renderChrome,
  isFormTouched,
  multiSelectFieldNames,
  backendFieldValidationMode,
  isEditing,
  isFieldReset,
  postalCodeMatches,
  setIsFormTouched,
  setShowDownloadQuoteFooter,
  roleLabels,
  role,
  formStepBackendKey,
  stepKeyMain,
  localeFrench,
  renderSectionExtras,
  sectionComponents,
  onUserEdit
}) {
  const channelName =
    (profile && profile.channel && profile.channel.name) || null;

  // Stage 4c-ii: `definition` spans EVERY step, not just the current one, so
  // `engine.values`/`fieldStates` are correct for the WHOLE order at all
  // times — the raw engine state (a plain `useState`) was ALREADY confirmed
  // to survive forward (Continue) navigation intact (this component doesn't
  // remount on Continue — only on Back, via formStepVulcan.js's `isLoading`
  // gate); the single-step `definition` was the only thing making
  // `fieldStates` forget an already-visited step's fields. Real-data bug this
  // fixes: `mergeEngineValuesIntoLegacyBag`'s overlay only ever saw the
  // CURRENT step's `fieldStates`, so a field from a step already passed
  // through submitted back as empty (findings #19–#21).
  //
  // `steps` (every raw Contentful step entry) is optional and falls back to
  // wrapping just `stepFields`/`stepName` as a single step — preserves every
  // existing test/caller that only ever exercised one step at a time; real
  // usage (formStepVulcan.js) always supplies the full array.
  const allRawSteps = useMemo(
    () =>
      steps && steps.length
        ? steps
        : [{ fields: { name: stepName, formStepFields: stepFields } }],
    [steps, stepFields, stepName]
  );

  const definition = useMemo(
    () => ({
      steps: allRawSteps.map(s => ({
        name: s.fields && s.fields.name,
        items: normalizeItems(
          (s.fields && s.fields.formStepFields) || [],
          vulcanContentTypeMap
        )
      }))
    }),
    [allRawSteps]
  );

  // Vulcan's own step numbering is 1-indexed (matches `step` everywhere else
  // in formStepVulcan.js/index.js); default to step 1 when unset (matches
  // every existing single-step test, which never passed `step` at all).
  const currentStepIndex = (step || 1) - 1;

  const salesRepOutlet =
    profile && profile.outlet ? profile.outlet.toString() : '';

  // Ancestor-hiding (imperative/declarative hides on a container) should
  // apply ORDER-WIDE now that the engine covers every step, not just
  // whichever one is currently on screen — flatten every step's raw fields,
  // not just the current step's `stepFields`.
  const allStepFields = useMemo(
    () =>
      allRawSteps.reduce(
        (acc, s) => acc.concat((s.fields && s.fields.formStepFields) || []),
        []
      ),
    [allRawSteps]
  );

  // `secureBusinessPlanVulcan`'s own raw options (with pricing labels) —
  // needed by `secureBusinessOffersVulcan`'s "3 Months Free" rule branch,
  // which isn't otherwise reachable there (`fieldRules` only ever sees the
  // field currently being evaluated, not other fields' definitions).
  const secureBusinessPlanOptions = useMemo(() => {
    const allLeafFields = definition.steps.reduce(
      (acc, s) => acc.concat(collectLeafFields(s.items)),
      []
    );
    const planField = allLeafFields.find(
      f => f.name === secureBusinessPlanVulcan
    );
    return (planField && planField.options) || [];
  }, [definition]);

  const fieldRules = useMemo(() => {
    const baseRules = createVulcanFieldRules({
      orderData,
      dealershipName,
      locale,
      t: strings,
      channelName,
      secureBusinessPlanOptions
    });
    // Vulcan's imperative hides are applied at the RENDER layer, but the engine
    // derives field state independently — so a REQUIRED field inside a hidden
    // container still failed validation and silently blocked Continue. Fold the
    // container hide into the field's own visibility so validation skips it,
    // mirroring the legacy validator's `else if (isVisible)`.
    const isHiddenByAncestor = makeHiddenAncestorPredicate({
      stepFields: allStepFields,
      orderData,
      salesRepOutlet,
      visibilityContext: {
        orderData,
        isFormTouched,
        multiSelectFieldNames,
        backendFieldValidationMode
      }
    });
    return (field, state, values) => {
      const patch = baseRules(field, state, values);
      if (
        isHiddenByAncestor(field, values) ||
        isGatedByEmptyRelatedField(field, values, orderData)
      ) {
        return { ...(patch || {}), visibility: false };
      }
      return patch;
    };
  }, [
    orderData,
    dealershipName,
    locale,
    strings,
    channelName,
    allStepFields,
    salesRepOutlet,
    isFormTouched,
    multiSelectFieldNames,
    backendFieldValidationMode,
    secureBusinessPlanOptions
  ]);
  const pricing = useMemo(() => createVulcanPricingAdapter(), []);
  // Default onSubmit = the §4.1-compliant Vulcan handler (create/update via
  // request.js + the step-2 file upload); a caller may still override via the
  // `onSubmit` prop.
  const submitHandler = useMemo(
    () =>
      onSubmit || createVulcanSubmitHandler({ orderData, currentStep: step }),
    [onSubmit, orderData, step]
  );
  const payload = useMemo(
    () =>
      createVulcanPayloadAdapter({ yes: (strings && strings.yes) || 'Yes' }),
    [strings]
  );
  // The section registry MUST stay referentially stable across renders.
  // Rebuilding it creates brand-new section component FUNCTIONS; React treats a
  // changed component type as a different element, so it UNMOUNTS and REMOUNTS
  // every section, wiping their local state. For UserDetailsForm that is
  // destructive: remounting re-runs its `useEffect(…, [isSameAs])` →
  // `populateFormFields()`, which with `isSameAs === false &&
  // isDefaultPopulated === false` takes the `else { fieldNewValue = '' }` branch
  // and CLEARS every field it owns (userDetailsForm.js:242). Since that write
  // calls `setIsFormTouched(true)`, depending on `isFormTouched` here created a
  // remount→clear→touch→remount cycle that blanked Signing Authority / Service
  // Address (confirmed real-data regression, order 83480 step 1).
  //
  // So the mutable visibility inputs (isFormTouched/multiSelectFieldNames/
  // backendFieldValidationMode) are deliberately NOT dependencies: the content-
  // type sections read them LIVE from `VulcanSectionChromeContext`
  // (`chrome.visibilityContext` below), which is re-provided every render.
  //
  // `orderData` is in that same category and is deliberately NOT a dependency
  // either. It changes identity on EVERY save (`setOrderData` rebuilds it as
  // `{...orderData, ...response.data}`, and `stableOrderData` in form/index.js
  // is only a null-fallback memo, not a deep compare), so depending on it here
  // remounted every section on every save — discarding their state and
  // re-running their mount effects, which call `touch()` ->
  // `setShowDownloadQuoteFooter(false)`. That is what made step 1's
  // "Download Quote PDF / Proceed with the order" footer revert to the plain
  // Continue footer the instant the save resolved (confirmed against a real
  // order: the flag was set true once, nothing reset it, and formStepVulcan
  // itself never remounted). The sections read it live from
  // `chrome.visibilityContext.orderData` instead — see vulcanSectionRegistry's
  // `useLiveOrderData`.
  const sectionRegistry = useMemo(
    () =>
      createVulcanSectionRegistry(
        {
          t: strings,
          isEditing,
          isFieldReset,
          channelName,
          postalCodeMatches,
          setIsFormTouched,
          setShowDownloadQuoteFooter
        },
        sectionComponents
      ),
    [
      strings,
      isEditing,
      isFieldReset,
      channelName,
      postalCodeMatches,
      setIsFormTouched,
      setShowDownloadQuoteFooter,
      sectionComponents
    ]
  );

  const open = useMemo(() => computeOpenPanels(stepFields, formErrors), [
    stepFields,
    formErrors
  ]);

  // Role-prefixed control label, parity of formStepVulcan.js:1823 (step-1 main,
  // English only).
  const formatControl = title => {
    const roleName = role && role.name;
    const label =
      formStepBackendKey === stepKeyMain &&
      locale !== localeFrench &&
      roleName &&
      roleLabels[roleName]
        ? `${roleLabels[roleName]} ${title}`
        : title;
    return <Typography variant={{ size: 'h2' }}>{label}</Typography>;
  };

  const chrome = useMemo(
    () => ({
      visibilityContext: {
        orderData,
        isFormTouched,
        multiSelectFieldNames,
        backendFieldValidationMode
      },
      formatControl,
      renderSectionExtras,
      // Needed by Vulcan's imperative item hides (prequalificationSectionHelpers
      // gates the TSB 1.5 sections on the rep's outlet).
      salesRepOutlet: profile && profile.outlet ? profile.outlet.toString() : ''
    }),
    // formatControl closes over stable-ish label inputs; recompute when they change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      orderData,
      isFormTouched,
      multiSelectFieldNames,
      backendFieldValidationMode,
      locale,
      role,
      formStepBackendKey,
      renderSectionExtras,
      salesRepOutlet
    ]
  );

  const config = useMemo(
    () => ({
      locale,
      strings,
      fieldRules,
      pricing,
      payload,
      onSubmit: submitHandler,
      initialValues,
      orderData,
      isFormTouched,
      multiSelectFieldNames,
      backendFieldValidationMode,
      adapterContext: { orderData, channelName }
    }),
    [
      locale,
      strings,
      fieldRules,
      pricing,
      payload,
      submitHandler,
      initialValues,
      orderData,
      isFormTouched,
      multiSelectFieldNames,
      backendFieldValidationMode,
      channelName
    ]
  );

  // Stage 4c-iii: calling `useFormRunner` directly (what `<FormRunner>` does
  // internally) instead of rendering `<FormRunner definition={...}>` — its
  // OWN `currentStepIndex` is meaningless now: `definition` spans every step
  // (4c-ii), but Vulcan's real current step is external/URL-driven (`step`
  // prop) with business-rule skip logic (e.g. `step + 2` for BusinessConnect
  // orders) the engine's internal `goNext`/`goPrev` (±1 only) can't express,
  // so nothing ever advances it — it would stay stuck validating/rendering
  // `engine.steps[0]` forever.
  const rawEngine = useFormRunner(definition, config);
  const currentStep = rawEngine.steps[currentStepIndex] || {
    items: [],
    name: undefined
  };
  const currentStepFields = useMemo(
    () => collectLeafFields(currentStep.items),
    [currentStep]
  );
  // Mirrors the package's own internal `revealErrors` (shown after a failed
  // Next) — `useFormRunner` doesn't expose a setter for its OWN version,
  // since it's meant to be driven by its OWN `goNext`, which we don't use.
  const [revealErrors, setRevealErrors] = useState(false);
  const errors = useMemo(() => {
    const stepErrors = validateVulcanStepFields(
      currentStepFields,
      rawEngine.fieldStates,
      locale,
      strings
    );
    // Bespoke Optik TV table rules (theme-pack count, STB limit, installation)
    // live on the table's own value, outside the declarative validation.
    currentStepFields.forEach(field => {
      const state = rawEngine.fieldStates[field.name];
      if (!state || state.type !== 'OptikTvTable' || !state.isVisible) return;
      const validation = state.value && state.value.validation;
      if (validation && !validation.isValid && validation.error) {
        stepErrors[field.name] = validation.error;
      }
    });
    return stepErrors;
  }, [currentStepFields, rawEngine.fieldStates, locale, strings]);
  const goNext = useCallback(() => {
    if (Object.keys(errors).length > 0) {
      setRevealErrors(true);
      return false;
    }
    setRevealErrors(false);
    return true;
  }, [errors]);
  // The engine marks every `setValue` as touched, including programmatic
  // writes (fieldRules-derived values pushed back by section bridges), which
  // surfaced required errors on untouched fields. Only real user edits count.
  const [userEditedFields, setUserEditedFields] = useState(() => new Set());
  const handleUserEdit = useCallback(
    (name, value) => {
      const result = onUserEdit ? onUserEdit(name, value) : undefined;
      if (result !== false && name) {
        setUserEditedFields(prev =>
          prev.has(name) ? prev : new Set(prev).add(name)
        );
      }
      return result;
    },
    [onUserEdit]
  );
  const fieldStates = useMemo(() => {
    const out = {};
    Object.keys(rawEngine.fieldStates).forEach(name => {
      const state = rawEngine.fieldStates[name];
      const edited = userEditedFields.has(name);
      out[name] = state.touched === edited ? state : { ...state, touched: edited };
    });
    return out;
  }, [rawEngine.fieldStates, userEditedFields]);
  // `currentStep`/`currentStepIndex`/`errors`/`revealErrors`/`goNext`
  // override the raw engine's OWN (unused) versions of the same fields, and
  // `fieldStates` differs only in `touched` (user edits only) —
  // everything else (`values`, `setValue`, `resolvedValues`,
  // `pricingResult`, `submitStatus`, `goPrev`/`submit`/`isLastStep`) passes
  // through unchanged; the latter three are only exercised by the DEFAULT
  // (non-`renderChrome`) fallback chrome below, never by real Vulcan usage.
  const engine = useMemo(
    () => ({
      ...rawEngine,
      fieldStates,
      currentStep,
      currentStepIndex,
      errors,
      revealErrors,
      goNext
    }),
    [
      rawEngine,
      fieldStates,
      currentStep,
      currentStepIndex,
      errors,
      revealErrors,
      goNext
    ]
  );

  const ctx = {
    engine,
    components: vulcanStructuralComponents,
    fieldRegistry: standardVulcanFieldRegistry,
    sectionRegistry
  };
  const body = renderItems(currentStep.items, ctx, currentStepIndex);

  // Default (non-chromeless) fallback — matches `<FormRunner>`'s OWN default
  // chrome exactly, just built directly here instead of by the package
  // component. Only exercised by tests that don't pass `renderChrome`; real
  // Vulcan usage (formStepVulcan.js) always does.
  const renderDefaultChrome = () => {
    const label = (key, fallback) => {
      const resolved = resolveString(key, locale, strings);
      return resolved === key ? fallback : resolved;
    };
    const handleSubmit = e => {
      e.preventDefault();
      if (engine.isLastStep) engine.submit();
      else engine.goNext();
    };
    return (
      <form onSubmit={handleSubmit} data-forms-core="form">
        {currentStep.name ? (
          <h2 data-forms-core="step-title">{currentStep.name}</h2>
        ) : null}
        {body}
        {(
          renderFooter ||
          (e => <VulcanPricingFooter pricingResult={e.pricingResult} />)
        )(engine)}
        <div data-forms-core="nav">
          {currentStepIndex > 0 ? (
            <button type="button" onClick={engine.goPrev}>
              {label('nav.back', 'Back')}
            </button>
          ) : null}
          <button type="submit" disabled={engine.submitStatus === 'submitting'}>
            {engine.isLastStep
              ? label('nav.submit', 'Submit')
              : label('nav.next', 'Next')}
          </button>
        </div>
        {engine.submitStatus === 'submitting' ? (
          <div role="status">{label('nav.submitting', 'Submitting…')}</div>
        ) : null}
      </form>
    );
  };

  return (
    <ExpandCollapse
      key={open.join('|') || 'empty'}
      topDivider={false}
      initialOpen={open}
    >
      {expandProps => (
        <VulcanSectionChromeContext.Provider value={{ ...chrome, expandProps }}>
          <VulcanUserEditContext.Provider value={handleUserEdit}>
            {renderChrome ? renderChrome(engine, body) : renderDefaultChrome()}
          </VulcanUserEditContext.Provider>
        </VulcanSectionChromeContext.Provider>
      )}
    </ExpandCollapse>
  );
}

VulcanFormStepRunner.propTypes = {
  stepFields: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  stepName: PropTypes.string,
  step: PropTypes.number,
  // Every raw Contentful step entry (same shape as formStepVulcan.js's
  // `stepData`, one per step) — optional; falls back to treating
  // `stepFields`/`stepName` as a single step when omitted (Stage 4c-ii).
  steps: PropTypes.arrayOf(PropTypes.shape({})),
  orderData: PropTypes.shape({}),
  profile: PropTypes.shape({
    channel: PropTypes.shape({ name: PropTypes.string }),
    role: PropTypes.shape({ name: PropTypes.string }),
    outlet: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  locale: PropTypes.string,
  strings: PropTypes.shape({ yes: PropTypes.string }),
  dealershipName: PropTypes.string,
  initialValues: PropTypes.shape({}),
  formErrors: PropTypes.shape({}),
  onSubmit: PropTypes.func,
  renderFooter: PropTypes.func,
  renderChrome: PropTypes.func,
  isFormTouched: PropTypes.bool,
  multiSelectFieldNames: PropTypes.arrayOf(PropTypes.string),
  backendFieldValidationMode: PropTypes.oneOf(['all', 'any']),
  isEditing: PropTypes.string,
  isFieldReset: PropTypes.bool,
  postalCodeMatches: PropTypes.bool,
  setIsFormTouched: PropTypes.func,
  setShowDownloadQuoteFooter: PropTypes.func,
  roleLabels: PropTypes.shape({}),
  role: PropTypes.shape({ name: PropTypes.string }),
  formStepBackendKey: PropTypes.string,
  stepKeyMain: PropTypes.string,
  localeFrench: PropTypes.string,
  renderSectionExtras: PropTypes.func,
  // Called on genuine user field edits — the engine equivalent of the app's
  // handleFormField "form was touched" side effect. See userEditContext.
  onUserEdit: PropTypes.func,
  // Component overrides forwarded to createVulcanSectionRegistry's DI param —
  // must be referentially stable (it is a registry dependency).
  sectionComponents: PropTypes.shape({})
};

VulcanFormStepRunner.defaultProps = {
  stepName: undefined,
  step: undefined,
  steps: undefined,
  orderData: {},
  profile: {},
  locale: 'EN',
  strings: {},
  dealershipName: undefined,
  initialValues: {},
  formErrors: {},
  onSubmit: undefined,
  renderFooter: undefined,
  renderChrome: undefined,
  isFormTouched: false,
  multiSelectFieldNames: [],
  backendFieldValidationMode: 'all',
  isEditing: '',
  isFieldReset: false,
  postalCodeMatches: false,
  setIsFormTouched: undefined,
  setShowDownloadQuoteFooter: undefined,
  roleLabels: {},
  role: undefined,
  formStepBackendKey: undefined,
  stepKeyMain: undefined,
  localeFrench: 'fr-CA',
  renderSectionExtras: undefined,
  onUserEdit: undefined,
  sectionComponents: undefined
};

export default VulcanFormStepRunner;
